import { Location } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { DirectSubscriptionService } from '@app/modules/direct/services/direct-subscription.service';
import { PayoutMethodProvider } from '@app/modules/direct/services/payout-methods.service';
import { AIFacade } from '@app/shared/+state/AI';
import { AIMode, GeneratedReply } from '@app/shared/+state/AI/AI.models';
import { ActivityFeedComponent } from '@app/shared/components/activity-feed/activity-feed.component';
import { DialogReviewGuestComponent } from '@app/shared/components/dialog-review-guest/dialog-review-guest.component';
import { LoadingState, NotificationType, Reservation, User } from '@app/shared/interfaces';
import { PosthogFeatureFlag } from '@app/shared/interfaces/lib/feature-flags.interface';
import {
  GuestPortalStatus,
  GuestVettingData,
  ReservationStatus,
  VerificationStatus,
} from '@app/shared/interfaces/lib/guestvetting.interface';
import { Channel } from '@app/shared/models/channel';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { Provider } from '@app/shared/services/guest-portal/guest-portal.service';
import { GuestVettingService } from '@app/shared/services/guest-vetting/guest-vetting.service';
import { IntercomService } from '@app/shared/services/intercom.service';
import { InternalCsService } from '@app/shared/services/internal-cs/internal-cs.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { PresenceService } from '@app/shared/services/presence/presence.service';
import {
  InboxUpdatePusherPayload,
  PresenceInfo,
  PresenceResourceType,
  PresenceType,
  PusherService,
} from '@app/shared/services/pusher/pusher.service';
import { ReservationService } from '@app/shared/services/reservation/reservation.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { SelectedReservationService } from '@app/shared/services/selected-reservation.service';
import { SentimentAnalysisService } from '@app/shared/services/sentiment-analysis/sentiment-analysis.service';
import { ThreadService as ThreadApiService, ThreadPayload } from '@app/shared/services/thread/thread.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { format } from 'date-fns';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest } from 'rxjs';
import { auditTime, filter, map, share, take, takeUntil } from 'rxjs/operators';
import { TributeOptions } from 'tributejs';

interface ImageUpload {
  src: string;
  file: File;
}

enum AISuggestionRating {
  LIKE = 'like',
  DISLIKE = 'dislike',
}

@Component({
  standalone: false,
  selector: 'sbnb-thread',
  templateUrl: './thread.component.html',
  styleUrls: ['./thread.component.scss'],
  providers: [SelectedReservationService],
})
export class ThreadComponent implements OnInit, OnDestroy {
  id: string;
  miniCalendarEvents: any[];
  newMessageContents = '';
  stickyNewMessage = false;
  today = new Date();
  thread: ThreadPayload;
  messages: any[];
  loading: boolean;
  reservationLoading: boolean;
  miniCalLoading: boolean;
  guestLoading: boolean;
  messageSending: boolean;
  window = window;
  cannedResponseOptions: any;
  numListOptionsForSearchBar = 10;
  searchCriteria: string;

  menuOpen = false;

  reservations: Reservation[];
  activeReservation: Reservation;

  isCS: boolean;

  debugMode$ = this.csService.debugMode;

  @ViewChild('newMessageContainer', { static: true })
  newMessageContainer: ElementRef;

  @ViewChild('cannedResponsesTrigger', { static: true })
  cannedResponsesTrigger: MatMenuTrigger;

  @ViewChild('searchInput', { static: true })
  searchInput: ElementRef;

  @ViewChild('newMessageTextarea', { static: false })
  newMessageTextarea: ElementRef;

  @ViewChild('messagesContainer', { static: false })
  messagesContainer: ElementRef;

  @ViewChild(ActivityFeedComponent) activityFeed: ActivityFeedComponent;

  perms$ = this.userPermissionsService;
  user: User = this.authService.getUserDetails();
  Features = Features;
  tributeOptions: TributeOptions<unknown>;

  isMobile$ = this.deviceDetectionService.isMobileViewport();

  isDirectOnboarded$ = this.directSubscriptionService.isOnboarded$;

  generateReplyLoadingState$: Observable<LoadingState | null>;
  generatedReply$: Observable<GeneratedReply | null>;
  generatedReplyMessage: string | null;
  generatedReplyUuid: string | null;
  improvedReplyUuid: string | null;
  private generatedReplyOrImprovement: GeneratedReply | null;

  generateImprovementLoadingState$: Observable<LoadingState | null>;

  private prewrittenAIMessage$ = new BehaviorSubject<GeneratedReply | null>(null);
  combinedAIMessage$: Observable<GeneratedReply | null>;

  sentAIFeedback: AISuggestionRating | null = null;

  imageLoading = false;
  imageUploads: ImageUpload[] = [];

  LoadingState = LoadingState;
  Channel = Channel;

  guestVettingData$: Observable<GuestVettingData>;

  // Data from the new GVS service - we need both during this transition period. Not ideal, but the backend can't easily syndicate it
  guestVettingNew = toSignal(this.selectedReservationService.guestVetting$);

  VerificationStatus = VerificationStatus;
  GuestPortalStatus = GuestPortalStatus;
  ReservationStatus = ReservationStatus;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  public grossRevenue$ = this.selectedReservationService.getReservationFinanceLineItem$('revenue');
  public undoMessage: string;

  canUploadImages$ = combineLatest([
    this.selectedReservationService.activeReservation$,
    this.selectedReservationService.threadData$,
    this.posthog.featureEnabled$(PosthogFeatureFlag.AirbnbSupportImages),
  ]).pipe(
    map(([reservation, thread, airbnbSupportImagesEnabled]) => {
      if (reservation) {
        return (
          (reservation.platform === 'airbnb' && reservation.status === 'Accepted') || reservation.platform !== 'airbnb'
        );
      }

      // If no reservation, check if this is an Airbnb Support thread (Airbnb support messages _do not_ have a reservation)
      return (
        airbnbSupportImagesEnabled &&
        thread?.platform === 'airbnb-official' &&
        thread?.status === 'Unknown status' &&
        thread?.guest.first_name === 'Airbnb Support'
      );
    })
  );

  aiSuggested = false;
  userHasEditedMessage = false;
  prewrittenMessageFetched = false;

  showImproveButton = false;
  intercomHideActionSuccess: boolean;

  public sentimentAnalysisEnabled$ = this.posthog.featureEnabled$(PosthogFeatureFlag.SentimentAnalysis);
  public autoReplyWorking = false;
  public autoReplyWorkingMessage = 'Auto Reply AI is thinking';
  public sentimentEnabled$ = this.sentimentAnalysisService.enabled$;

  // Add new properties for presence tracking
  usersTyping: PresenceInfo[] = [];
  usersViewing: PresenceInfo[] = [];

  // Add flag to track if user has started typing
  private hasStartedTyping = false;

  shouldShowGuestInsights = computed(() => {
    // If the reservation is Direct Premium, then hide insights if the provider is GVS, and not Autohost
    if (
      this.activeReservation?.platform === Channel.Direct &&
      this.activeReservation?.payment_provider === PayoutMethodProvider.ADYEN
    ) {
      return this.guestVettingNew()?.provider === Provider.AUTOHOST;
    } else {
      // For non-Direct premium, always true
      return true;
    }
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reservationService: ReservationService,
    private threadApiService: ThreadApiService,
    private titleService: TitleService,
    private toast: ToastNotificationsService,
    private authService: AuthenticationService,
    public userPermissionsService: UserPermissionsService,
    private csService: InternalCsService,
    private location: Location,
    private segmentIoService: SegmentIoService,
    private posthog: PosthogService,
    private pusher: PusherService,
    private deviceDetectionService: DeviceDetectionService,
    private directSubscriptionService: DirectSubscriptionService,
    private AIFacade: AIFacade,
    private selectedReservationService: SelectedReservationService,
    private guestVettingService: GuestVettingService,
    private intercomService: IntercomService,
    private dialog: MatDialog,
    private presenceService: PresenceService,
    private sentimentAnalysisService: SentimentAnalysisService
  ) {
    this.selectedReservationService.getThreadDataFromThread();

    this.combinedAIMessage$ = combineLatest([this.prewrittenAIMessage$, this.AIFacade.generatedReply$]).pipe(
      map(([prewritten, generated]) => {
        if (generated) {
          return { ...generated, is_prewritten: false };
        }
        if (prewritten) {
          return { ...prewritten, is_prewritten: true };
        }
        return null;
      })
    );
  }

  ngOnInit() {
    this.AIFacade.resetGeneratedReply();
    this.AIFacade.resetGeneratedImprovement();

    this.generateReplyLoadingState$ = this.AIFacade.generateReplyLoadingState$;
    this.generatedReply$ = this.AIFacade.generatedReply$;

    this.generateImprovementLoadingState$ = this.AIFacade.generatedImprovementLoadingState$;
    this.AIFacade.generatedImprovement$.subscribe((generatedImprovement) => {
      if (generatedImprovement && generatedImprovement.reply.length) {
        this.improvedReplyUuid = generatedImprovement.uuid;

        this.generatedReplyOrImprovement = generatedImprovement;

        this.segmentIoService.track(
          SegmentEvent.AIDraftMessageImproved,
          this.generateSegmentMetadata(generatedImprovement)
        );

        this.undoMessage = this.newMessageContents;

        this.newMessageContents = generatedImprovement.reply;
      }
    });

    this.generatedReply$.pipe(takeUntil(this.destroyed$)).subscribe((generatedReply) => {
      if (generatedReply && generatedReply.reply.length && !this.generatedReplyUuid) {
        // see this.combinedAIMessage$.pipe below

        this.segmentIoService.track(SegmentEvent.AIDraftMessageGenerated, this.generateSegmentMetadata(generatedReply));
      }
    });

    this.combinedAIMessage$.pipe(takeUntil(this.destroyed$)).subscribe((message) => {
      if (message && !this.userHasEditedMessage) {
        this.generatedReplyMessage = message.reply;

        // Only set the UUID if it's a newly generated message (not an improvement)
        if (!this.improvedReplyUuid && !this.generatedReplyUuid) {
          this.generatedReplyUuid = message.uuid;
        }

        this.generatedReplyOrImprovement = message;

        this.newMessageContents = message.reply;

        // We want to scroll to the *top*, not the bottom of
        // the text area after an AI generation. The customer
        // will not be reading from the bottom up.
        this.scrollNewMessageTextAreaContentsToTop();

        this.aiSuggested = true;
      }
    });

    this.isCS = this.csService.isCS();

    this.titleService.manuallySet('Conversation');
    this.loading = true;
    this.reservationLoading = true;
    this.miniCalLoading = true;
    this.guestLoading = true;

    this.route.params.subscribe((params) => {
      this.id = params['id'];

      // Handle any bad URL values by redirecting them to the default
      if (!this.id) {
        this.router.navigate(['/inbox/all']);
      }
    });
    this.selectedReservationService.threadData$
      .pipe(
        takeUntil(this.destroyed$),
        filter((thread) => Boolean(thread))
      )
      .subscribe((res) => {
        this.initThread(res);
      });

    // Subscribe to presence updates for this thread
    try {
      if (this.user.beta_features_enabled) {
        this.presenceService
          .getUsersTypingInThread(this.id)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((users) => {
            this.usersTyping = users;
          });

        this.presenceService
          .getUsersViewingThread(this.id)
          .pipe(takeUntil(this.destroyed$))
          .subscribe((users) => {
            this.usersViewing = users;
          });
      }
    } catch (error) {
      console.error('Error subscribing to presence updates:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();

    this.AIFacade.resetGeneratedReply();

    // They're no longer viewing this thread.
    try {
      this.presenceService.sendPresenceUpdate(PresenceType.Idle, null, PresenceResourceType.Conversations).subscribe();
    } catch (error) {
      console.error('Error sending presence update:', error);
    }
  }

  openEvaluateGuestDialog() {
    this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      this.dialog.open(DialogReviewGuestComponent, {
        width: isMobile ? '100%' : '720px',
        height: isMobile ? '100%' : 'auto',
        maxWidth: isMobile ? '100vw' : '720px',

        data: {
          reservationUuid: this.activeReservation.uuid,
          guestName: this.getGuestName(),
        },
      });
    });
  }

  private getGuestName(): string {
    const firstName = this.thread.guest.custom_first_name || this.thread.guest.first_name;
    const lastName = this.thread.guest.custom_last_name || this.thread.guest.last_name;

    if (firstName || lastName) {
      return `${firstName} ${lastName}`;
    }

    return '';
  }

  onGuestNameUpdated(event: { firstname: string; lastname: string }) {
    this.titleService.manuallySet(`${event.firstname} ${event.lastname}`);

    this.thread.guest.custom_first_name = event.firstname;
    this.thread.guest.custom_last_name = event.lastname;
  }

  initThread(res): void {
    if (res.error) {
      this.router.navigate(['/inbox']);
      return;
    }

    this.thread = res;
    this.guestLoading = false;

    this.segmentIoService.track(SegmentEvent.VisitedThread, {
      thread_id: this.thread.thread_id,
      platform: this.thread.platform,
      status: this.thread.status_key,
    });

    this.titleService.manuallySet(this.getGuestName());

    this.getMessages();
    this.getReservations();
    this.registerForConversationMessagingUpdatesFromPusher(this.id);
    this.listenForPusherPresenceUpdates();
    this.listenForGenerateReply();

    // Mark the thread as read
    if (this.thread.unread && !this.authService.isCS()) {
      this.threadApiService.markAsRead(this.id).subscribe((res) => {});
    }

    this.route.queryParams.subscribe((params) => {
      if (params['draft'] === 'true') {
        this.AIFacade.generateReply(this.thread.uuid, this.thread.host.user_id, null);
      }
    });

    // Get a list of appropriate canned responses
    this.threadApiService.getCanned(false, this.id).subscribe((res) => {
      this.cannedResponseOptions = res.data;

      this.tributeOptions = {
        trigger: '/',
        values: this.cannedResponseOptions,
        menuItemTemplate: (item) => {
          return `<div style="margin-bottom: 8px"><strong>${(item.original as { name: string }).name}</strong></div><div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${(item.original as { rule_preview: string }).rule_preview}</div>`;
        },
        selectTemplate: function (item) {
          return '';
        },
        lookup: 'name',
        noMatchTemplate: () => '<div style="padding: 8px 16px; font-size: 14px">No canned responses found.</div>',
      };
    });
  }

  listenForGenerateReply() {
    this.pusher.generateReply.subscribe(({ content, thread_uuid }) => {
      if (this.thread.uuid !== thread_uuid) {
        return;
      }

      // Workaround because init is called twice and all the tokens are duplicated otherwise (e.g. ThanksThanks)
      let latestToken = this.newMessageContents.substring(this.newMessageContents.length - content.length);
      if (content !== latestToken) {
        this.newMessageContents += content;
      }
    });
  }

  // TODO: if more than 1 minute since last AI presence update, set autoReplyWorking to false
  // TODO: combine with user presence updates code
  listenForPusherPresenceUpdates() {
    this.pusher.presenceUpdate$
      .pipe(/*auditTime(5000), */ takeUntil(this.destroyed$))
      .subscribe((res: PresenceInfo) => {
        if (res.resource_id !== this.thread.uuid || res.resource_type !== PresenceResourceType.Conversations) {
          return;
        }

        switch (res.type) {
          case PresenceType.InboxAIDecidingHowToProceed:
            this.autoReplyWorking = true;
            this.autoReplyWorkingMessage = 'Auto Reply AI is thinking';
            break;
          case PresenceType.InboxAIWritingAutoReply:
          case PresenceType.InboxAIAssessingAutoReply:
            this.autoReplyWorking = true;
            this.autoReplyWorkingMessage = 'Auto Reply AI is working';
            break;
          case PresenceType.InboxAIWritingAutoReplySucceeded:
          case PresenceType.InboxAIWritingAutoReplyFailed:
          case PresenceType.InboxAIWritingAutoReplyAborted:
            this.autoReplyWorking = false;
            break;
        }
      });
  }

  registerForConversationMessagingUpdatesFromPusher(uuid: string): void {
    this.pusher
      .getConversationUpdatesForAThreadUuid(uuid)
      .pipe(auditTime(5000), takeUntil(this.destroyed$))
      .subscribe((res: InboxUpdatePusherPayload) => {
        // Refetch messages
        this.getMessages();

        // Mark the thread as read
        if (!this.authService.isCS()) {
          this.threadApiService.markAsRead(this.id).subscribe((res) => {});
        }
      });
  }

  public getMessages() {
    if (
      !this.prewrittenMessageFetched &&
      !(this.thread.platform === Channel.Booking && this.thread.supports.bdc_rtb_messaging === false)
    ) {
      this.threadApiService.getPrewrittenAIMessage(this.id).subscribe((res) => {
        this.prewrittenAIMessage$.next(res);
        this.prewrittenMessageFetched = true;

        if (res) {
          this.segmentIoService.track(SegmentEvent.AIPrewrittenDraftMessageViewed, this.generateSegmentMetadata(res));
        }
      });
    }

    this.threadApiService.getMessages(this.id).subscribe((res) => {
      this.messages = res;
      this.loading = false;

      setTimeout(() => {
        this.scrollToMostRecentMessage();
      }, 10);
    });
  }

  public onMessageResize() {
    // If the user has scrolled way up, we won't interfere with their scroll position,
    // but if they are near the bottom, we will scroll to keep the last message in view.
    if (!this.messagesContainer || !this.newMessageContainer) {
      return;
    }

    const isNearBottom =
      this.messagesContainer.nativeElement.scrollHeight -
        this.messagesContainer.nativeElement.clientHeight -
        this.messagesContainer.nativeElement.scrollTop <=
      this.newMessageContainer.nativeElement.clientHeight + 10; // 10 pixels to give some buffer

    if (isNearBottom) {
      this.scrollToMostRecentMessage();
    }
  }

  getReservations() {
    this.reservationService
      .getReservationsForThread(this.id, { with_scheduled_messages_paused_status: true })
      .subscribe((res) => {
        this.reservations = res;
        this.reservationLoading = false;

        if (this.reservations[0]) {
          // This "active registration" can also be an inquiry, which doesn't have a uuid
          this.activeReservation = this.reservations[0];

          if (this.activeReservation.uuid) {
            if (this.activeReservation.autohost?.id) {
              this.guestVettingData$ = this.threadApiService
                .getGuestVettingData(this.activeReservation.uuid)
                .pipe(share());
            }

            // For new components, don't rely on the presence of this mutated data.
            this.guestVettingService
              .fetchBasicAutohostDataForReservation(this.activeReservation.uuid)
              .subscribe((autohostData) => {
                this.activeReservation.autohost = autohostData;
              });
          }

          this.selectedReservationService.setActiveReservation(this.activeReservation);

          this.reservationService
            .getReservationBlocksForThread(this.id, this.activeReservation.listing_id)
            .subscribe((res) => {
              this.miniCalendarEvents = res;

              this.miniCalLoading = false;
            });
        } else {
          this.miniCalLoading = false;
        }
      });
  }

  goBack() {
    // this.router.navigate(['/inbox/segments/default']);
    this.location.back();
  }

  private createOptimisticSentMessage() {
    this.messageSending = true;

    // For optimistic loading we create a stub of the message to display in the GUI before the message is even accepted by the API
    const newMessage = {
      is_automated: false,
      automation: null,
      created: new Date(),
      entity_type: 'chat',
      from: {
        first_name: this.thread.host.name,
        has_profile_image: true,
        locale: 'en',
        name: this.thread.host.name,
        picture_url: this.thread.host.image,
        thumbnail_url: this.thread.host.image,
      },
      id: new Date().getTime() / 1000,
      intents: [],
      is_guest: false,
      message: this.newMessageContents,
      sorted_by: new Date(),
      pending: true,
    };

    this.messages = [newMessage, ...this.messages];

    setTimeout(() => {
      this.scrollToMostRecentMessage();
    }, 0);

    return newMessage.id;
  }

  // todo: beta

  generateSegmentMetadata(generatedReply?: GeneratedReply | null): object {
    try {
      if (this.thread) {
        let res = {
          conversation_uuid: this.thread.uuid,
          platform: this.thread.platform,
        };

        // Just stuff the full payload with one change into the segment metadata.
        if (generatedReply) {
          res = {
            ...res,
            ...generatedReply,
          };
          delete res['uuid'];
          delete res['reply'];
          res['prompt_uuid'] = generatedReply.uuid;
        }

        return res;
      }

      return {};
    } catch (err) {
      this.segmentIoService.track(SegmentEvent.ChatGPTDebugError, {
        function: 'generateSegmentMetadata',
      });
      console.error(err);
      return {};
    }
  }

  sendMessage(resending = false, id = 0) {
    const messageId = resending ? id : this.createOptimisticSentMessage();
    const sentMessageIndex = this.messages.findIndex((msg) => msg.id === messageId);
    const message = resending ? this.messages[sentMessageIndex].message : this.newMessageContents;
    const isGeneratedReply =
      this.generatedReplyMessage && this.sanitizeText(this.generatedReplyMessage) === this.sanitizeText(message);
    const preparedImageUploads = this.imageUploads ? this.imageUploads.map((upload) => upload.file) : [];
    this.newMessageContents = '';

    if (!resending && isGeneratedReply) {
      this.trackGeneratedReplySent();
    }

    this.hasStartedTyping = false;

    // Prioritize the original generated UUID over improvement UUID
    const uuidToSend = this.generatedReplyUuid || this.improvedReplyUuid;

    this.threadApiService
      .sendMessage(this.id, message, this.thread.host.user_id, uuidToSend, preparedImageUploads)
      .subscribe(
        (res) => {
          if (!res) {
            return;
          }

          this.resetMessageSendingState();

          if (sentMessageIndex === -1) {
            return;
          }

          this.messages[sentMessageIndex].pending = false;

          if (res.error) {
            this.messages[sentMessageIndex].failed = true;
          }
        },
        (error) => {
          this.messageSending = false;
          if (sentMessageIndex === -1) {
            return;
          }

          this.messages[sentMessageIndex].failed = true;
        }
      );

    this.imageUploads = [];
  }

  private sanitizeText(text: string | null | undefined) {
    if (text === null || text === undefined) {
      return null;
    }

    return text
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private resetMessageSendingState() {
    this.newMessageContents = '';
    this.messageSending = false;
    this.trackMessageSent();
    this.generatedReplyMessage = null;
    this.clearGeneratedReplyOrImprovement();
    this.clearUndoMessage();
    this.sentAIFeedback = null;
    this.AIFacade.resetGeneratedReply();
    this.AIFacade.resetGeneratedImprovement();
    this.generatedReplyUuid = null;
    this.improvedReplyUuid = null;
    this.hasStartedTyping = false; // Reset typing flag
  }

  private trackGeneratedReplySent() {
    this.segmentIoService.track(SegmentEvent.AIEngaged, {
      engagement_type: 'generated reply sent as is',
    });

    this.segmentIoService.track(
      SegmentEvent.AIDraftMessageSentFromTheInbox,
      this.generateSegmentMetadata(this.generatedReplyOrImprovement)
    );
  }

  private trackMessageSent() {
    this.segmentIoService.track(SegmentEvent.MessageSentFromTheInbox, this.generateSegmentMetadata());

    if (this.deviceDetectionService.isIosApp() || this.deviceDetectionService.isAndroidApp()) {
      this.segmentIoService.track(SegmentEvent.MessageSentFromTheInboxMobile, this.generateSegmentMetadata());
    }
  }

  resendFailedMessage(id: any) {
    const index = this.messages.findIndex((msg) => msg.id === id);
    this.messages[index].pending = true;
    this.sendMessage(true, id);
  }

  changeActiveReservation(activeIndex: number) {
    this.activeReservation = this.reservations[activeIndex];
    this.selectedReservationService.setActiveReservation(this.activeReservation);
  }

  applyCannedResponse(cr) {
    this.segmentIoService.track(SegmentEvent.UsedCannedResponse, {
      canned_response_id: cr.id,
    });

    this.threadApiService.prepareCannedResponse(this.id, cr.id, this.activeReservation.code).subscribe((res) => {
      if (res.error) {
        this.toast.open(`Error applying Canned response`, 'Dismiss', NotificationType.Error);
        return;
      }

      this.newMessageContents += res;

      // Scroll the textarea to the bottom of its contents
      this.scrollNewMessageTextAreaContentsToBottom();
    });
    this.searchCriteria = '';
  }

  private scrollToMostRecentMessage() {
    if (!this.messagesContainer) {
      return;
    }

    this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
  }

  private scrollNewMessageTextAreaContentsToBottom() {
    setTimeout(() => {
      this.newMessageTextarea.nativeElement.scrollTop = this.newMessageTextarea.nativeElement.scrollHeight;
    }, 100);
  }

  private scrollNewMessageTextAreaContentsToTop() {
    setTimeout(() => {
      this.newMessageTextarea.nativeElement.scrollTop = 0;
    }, 100);
  }

  filtersMatchSearchCriteria(option, searchCriteria) {
    if (!searchCriteria || searchCriteria === '') {
      return true;
    }

    return option.name.toLowerCase().includes(searchCriteria.toLowerCase());
  }

  reservationNeedsUpdating(payload: { message: any; res: any }) {
    this.ngOnInit();
  }

  focusCannedResponsesSearch() {
    setTimeout(() => {
      const res = document.querySelector('.filters__input-list-search');
      (res as any)?.focus();
    }, 100);
  }

  dateClicked(date: string, propertyId: string) {
    if (propertyId && date) {
      this.router.navigateByUrl(
        '/calendar/property/' +
          propertyId +
          '?month=' +
          format(new Date(date), 'MM') +
          '&year=' +
          format(new Date(date), 'YYYY')
      );
    }
  }

  onAIGenerateResponseClicked(
    threadUuid: string,
    hostUid: string | null,
    mode: AIMode,
    existingMessage: string | null
  ) {
    if (!threadUuid) {
      return;
    }

    // Send presence update for AI interactions
    this.sendTypingPresenceUpdate(threadUuid);

    switch (mode) {
      case AIMode.SUGGEST:
        this.AIFacade.generateReply(threadUuid, hostUid, this.generatedReplyUuid);
        break;

      case AIMode.IMPROVE:
        if (existingMessage?.trim()?.length) {
          this.AIFacade.improveReply(threadUuid, existingMessage);
        }
        break;
    }
  }

  clearGeneratedMessage() {
    this.segmentIoService.track(
      SegmentEvent.AISuggestionCleared,
      this.generateSegmentMetadata(this.generatedReplyOrImprovement)
    );

    this.newMessageContents = '';
    this.aiSuggested = false; // Reset the state
    this.generatedReplyMessage = null; // Clear the generated reply message
    this.clearGeneratedReplyOrImprovement();
  }

  onDroppedFile(fileList: FileList) {
    this.processImages(fileList);
  }

  onImagePasted(file: File) {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);

    this.processImages(dataTransfer.files);
  }

  onImageInputChange(imageInput: HTMLInputElement) {
    const files: FileList = imageInput.files;

    this.processImages(files);
  }

  private processImages(files: FileList) {
    this.imageLoading = true;

    if (!files || files.length === 0 || files.length > 10) {
      return;
    }

    for (let i = 0; i < files.length; i++) {
      if (!this.isValidImage(files[i])) {
        continue;
      }

      const reader = new FileReader();

      reader.addEventListener('load', (event: ProgressEvent<FileReader>) => {
        const result = event.target?.result as string;

        this.imageUploads.push({
          src: result,
          file: files[i],
        });
      });
      reader.readAsDataURL(files[i]);
    }

    this.imageLoading = false;
  }

  private isValidImage(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      this.toast.open('Invalid file type. Please upload a JPEG or PNG file.', 'Dismiss', NotificationType.Error);
      return false;
    }

    if (file.size > maxSizeInBytes) {
      this.toast.open('File size exceeds 5MB. Please upload a smaller image.', 'Dismiss', NotificationType.Error);
      return false;
    }

    return true;
  }

  removeImage(index) {
    this.imageUploads.splice(index, 1);
  }

  clearUndoMessage() {
    this.undoMessage = null;
  }

  clearGeneratedReplyOrImprovement() {
    this.generatedReplyOrImprovement = null;
  }

  undoImprovement() {
    this.segmentIoService.track(SegmentEvent.AIUndoImprovement, {
      user_entered_message: this.undoMessage,
      improved_message: this.newMessageContents,
      model: this.generatedReplyOrImprovement?.model,
    });

    this.newMessageContents = this.undoMessage;
    this.clearUndoMessage();
    this.clearGeneratedReplyOrImprovement();
  }

  public onAISuggestionLiked() {
    if (this.sentAIFeedback) {
      return;
    }

    this.sentAIFeedback = AISuggestionRating.LIKE;

    this.segmentIoService.track(
      SegmentEvent.AISuggestionLiked,
      this.generateSegmentMetadata(this.generatedReplyOrImprovement)
    );
  }

  public onAISuggestionDisliked() {
    if (this.sentAIFeedback) {
      return;
    }

    this.sentAIFeedback = AISuggestionRating.DISLIKE;

    const metadata = this.generateSegmentMetadata(this.generatedReplyOrImprovement);

    this.segmentIoService.track(SegmentEvent.AISuggestionDisliked, metadata);

    setTimeout(() => {
      if (this.router.url.indexOf('/inbox/thread/' + this.id) > -1) {
        this.posthog.capture(SegmentEvent.AISuggestionSurveyTriggered, metadata);
      }
    }, 500);
  }

  cancelAutoReplyAI() {
    // todo
  }

  onNewMessageInput() {
    this.userHasEditedMessage = true;
    if (this.newMessageContents.trim().length == 0) {
      this.aiSuggested = false;
      this.userHasEditedMessage = false;
      this.showImproveButton = false;
    } else if (!this.generatedReplyMessage || this.newMessageContents !== this.generatedReplyMessage) {
      this.showImproveButton = true;
    }
  }

  onMessageInput(): void {
    // Call existing input handler if it exists
    if (this.onNewMessageInput) {
      this.onNewMessageInput();
    }

    // Send typing presence update only on first type
    if (!this.hasStartedTyping && this.thread?.uuid) {
      this.hasStartedTyping = true;
      this.sendTypingPresenceUpdate(this.thread.uuid);
    }
  }

  sendTypingPresenceUpdate(threadUuid: string) {
    try {
      this.presenceService
        .sendPresenceUpdate(PresenceType.InboxTypingMessage, threadUuid, PresenceResourceType.Conversations)
        .subscribe();
    } catch (error) {
      console.error('Error sending presence update:', error);
    }
  }
}
